import { createControlId, destructureControlId, HpDestructuredControlIdResult } from '@avantia/questionset-model';
import { HpDynamicButtonClickPayload } from '../../actions/dynamicButtonClickActionTypes';
import { HpEventPayload, HpGetEventFunction } from '../../actions/eventActionTypes';
import { HpNavigationChangedPayload } from '../../actions/navigationChangedActionTypes';
import { getEventDetail } from '../../tools/utilities';
import {
  HpClientQuestion,
  HpEventTypes,
  HpFieldChangedPayload,
  HpGetClientQuestionSetStateFunction
} from '../../types';
import { HpFormName } from '../../types/actionTypes';
import {
  HpDateDropDownEventDetail,
  HpEventDetail,
  HpEventDetailTypes,
  HpNavTabEventDetail
} from '../../types/inputControlTypes';
import { HpQuestionSetPageProps } from '../../types/questionSetPagePropTypes';
import { HpEventTypeProcessor, HpHandleFormEventProps, HpHandleMouseEventProps } from './eventProcessor';

export class StandardFieldProcessor implements HpEventTypeProcessor<HpQuestionSetPageProps> {
  canHandle(detailType: HpEventDetailTypes): boolean {
    return (
      detailType === 'standard' ||
      detailType === 'nav-bar' ||
      detailType === 'questionset' ||
      detailType === 'button-action' ||
      detailType === 'date-drop-down' ||
      detailType === 'help-icon'
    );
  }

  handleFocusEvent(): void {
    throw new Error('Method not implemented.');
  }

  handleMouseEvent({ event, detail, pageProps, formName }: HpHandleMouseEventProps<HpQuestionSetPageProps>): void {
    const getQuestionSet: HpGetClientQuestionSetStateFunction = () => pageProps;
    const getEvent: HpGetEventFunction = () => event;
    const { target, targetId } = getEventDetail(event);
    const { question, type: detailType } = detail;
    const { questionId, groupNumber, indexes } = destructureControlId(targetId);
    let { key } = destructureControlId(target.id);
    if (detailType === 'button-action') {
      const payload: HpDynamicButtonClickPayload = {
        getEvent,
        questionId,
        key,
        indexes,
        groupNumber,
        formName,
        pageProps,
        question
      };
      pageProps.dynamicButtonClick(payload);
    } else if (detail.type === 'date-drop-down' && key) {
      const { fullDate } = detail as HpDateDropDownEventDetail;
      key = fullDate;
    }

    const payload: FieldChangedCommonProps = {
      getEvent,
      pageProps,
      formName,
      questionId,
      key,
      indexes,
      groupNumber,
      question,
      detail,
      getQuestionSet
    };
    this.fieldChangedCommon(payload);
  }

  handleFormEvent({ event, detail, pageProps, formName }: HpHandleFormEventProps<HpQuestionSetPageProps>): void {
    const getQuestionSet: HpGetClientQuestionSetStateFunction = () => pageProps;
    const getEvent: HpGetEventFunction = () => event;
    const { targetId, eventType: eventTypeString } = getEventDetail(event);
    const eventType = eventTypeString as HpEventTypes;
    const { question, type: detailType } = detail;
    const { questionId, key, groupNumber, indexes } = destructureControlId(targetId, {
      requirePrefix: !targetId.startsWith('HI')
    });

    if (detailType === 'nav-bar') {
      const { activeKey } = detail as HpNavTabEventDetail;
      const payload: HpNavigationChangedPayload = { getEvent, activeKey, formName, detail };
      pageProps.navigationChanged(payload);
    } else if (detailType === 'help-icon') {
      pageProps.helpClick({ eventType, question, getEvent });
    } else if (detailType === 'button-action') {
      const payload: HpDynamicButtonClickPayload = {
        getEvent,
        questionId,
        key,
        indexes,
        groupNumber,
        formName,
        pageProps,
        question
      };
      pageProps.dynamicButtonClick(payload);
    } else if (detailType === 'date-drop-down') {
      const { fullDate } = detail as HpDateDropDownEventDetail;
      const eventDetail = { ...detail, fullDate: key, dateDropDownId: targetId } as HpDateDropDownEventDetail;
      const payload: HpFieldChangedPayload = {
        getEvent,
        key: targetId,
        value: fullDate,
        formName,
        error: '',
        question,
        detail: eventDetail,
        getQuestionSet
      };
      pageProps.fieldChanged(payload);
    } else if (detailType === 'questionset') {
      const payload: HpFieldChangedPayload = {
        getEvent,
        key: 'questionSetId',
        value: key as string,
        formName,
        error: '',
        question,
        detail,
        getQuestionSet
      };
      pageProps.fieldChanged(payload);
    } else {
      const payload: FieldChangedCommonProps = {
        getEvent,
        pageProps,
        formName,
        questionId,
        key,
        indexes,
        groupNumber,
        question,
        detail,
        getQuestionSet
      };
      this.fieldChangedCommon(payload);
    }
  }

  handleKeyboardEvent(): void {
    throw new Error('Method not implemented.');
  }

  private fieldChangedCommon({
    detail,
    formName,
    getEvent,
    getQuestionSet,
    groupNumber,
    indexes,
    key,
    pageProps,
    question,
    questionId
  }: FieldChangedCommonProps): void {
    const { validationResult } = pageProps;
    const input = getEvent().target as HTMLInputElement;
    let { value } = input;
    if (key) {
      value = key as string;
    }

    if (groupNumber !== undefined) {
      questionId = createControlId({ questionId, groupNumber, indexes });
    }

    const error = validationResult.errors[questionId];
    const payload: HpFieldChangedPayload = {
      getEvent,
      key: questionId,
      value,
      error,
      formName,
      question,
      detail,
      getQuestionSet
    };
    pageProps.fieldChanged(payload);
  }
}

interface FieldChangedCommonProps extends HpDestructuredControlIdResult, HpEventPayload {
  pageProps: HpQuestionSetPageProps;
  formName: HpFormName;
  question: HpClientQuestion;
  detail: HpEventDetail;
  getQuestionSet: HpGetClientQuestionSetStateFunction;
}
